/**
 * Advanced Caching Service with Intelligent Invalidation and Performance Monitoring
 */

/**
 * Cache configuration and constants
 */
const CACHE_CONFIG = {
  // Cache TTL values in milliseconds
  TTL: {
    BUDGETS: 60 * 1000,           // 1 minute
    LEDGERS: 60 * 1000,           // 1 minute
    PAYEES: 5 * 60 * 1000,        // 5 minutes (longer for autocomplete)
    BUDGET_BALANCES: 30 * 1000,   // 30 seconds
    LEDGER_BALANCES: 30 * 1000,   // 30 seconds
    REPORTS: 2 * 60 * 1000,       // 2 minutes
    PAYDAY: 24 * 60 * 60 * 1000   // 24 hours
  },
  
  // Cache warming configuration
  WARM_UP: {
    ENABLED: true,
    DELAY: 5 * 1000,              // 5 seconds after initial load
    BACKGROUND_REFRESH: true
  },
  
  // Performance monitoring
  METRICS: {
    ENABLED: true,
    SAMPLE_RATE: 0.1              // 10% of operations
  }
};

/**
 * Cache key generators
 */
const CacheKeys = {
  BUDGETS: () => 'budgets_list',
  BUDGET: (id) => `budget_${id}`,
  BUDGET_BALANCE: (id) => `budget_balance_${id}`,
  BUDGET_LINES: (id, params = {}) => {
    const paramStr = Object.keys(params).length > 0 ? `_${JSON.stringify(params)}` : '';
    return `budget_lines_${id}${paramStr}`;
  },
  
  LEDGERS: () => 'ledgers_list',
  LEDGER: (id) => `ledger_${id}`,
  LEDGER_BALANCE: (id) => `ledger_balance_${id}`,
  LEDGER_LINES: (id, params = {}) => {
    const paramStr = Object.keys(params).length > 0 ? `_${JSON.stringify(params)}` : '';
    return `ledger_lines_${id}${paramStr}`;
  },
  
  PAYEES: () => 'payees_list',
  PAYDAY: () => 'next_payday',
  
  // Dependency tracking keys
  DEPENDENCIES: (key) => `deps_${key}`,
  METRICS: (key) => `metrics_${key}`
};

/**
 * Cache performance metrics
 */
class CacheMetrics {
  constructor() {
    this.metrics = {
      hits: 0,
      misses: 0,
      sets: 0,
      invalidations: 0,
      errors: 0,
      totalResponseTime: 0,
      operations: 0
    };
  }

  recordHit(responseTime = 0) {
    this.metrics.hits++;
    this.metrics.totalResponseTime += responseTime;
    this.metrics.operations++;
  }

  recordMiss(responseTime = 0) {
    this.metrics.misses++;
    this.metrics.totalResponseTime += responseTime;
    this.metrics.operations++;
  }

  recordSet() {
    this.metrics.sets++;
  }

  recordInvalidation() {
    this.metrics.invalidations++;
  }

  recordError() {
    this.metrics.errors++;
  }

  getStats() {
    const total = this.metrics.hits + this.metrics.misses;
    const hitRate = total > 0 ? (this.metrics.hits / total * 100).toFixed(2) : 0;
    const avgResponseTime = this.metrics.operations > 0 
      ? (this.metrics.totalResponseTime / this.metrics.operations).toFixed(2) 
      : 0;

    return {
      ...this.metrics,
      hitRate: `${hitRate}%`,
      averageResponseTime: `${avgResponseTime}ms`,
      totalOperations: total
    };
  }

  reset() {
    this.metrics = {
      hits: 0,
      misses: 0,
      sets: 0,
      invalidations: 0,
      errors: 0,
      totalResponseTime: 0,
      operations: 0
    };
  }
}

/**
 * Advanced cache service with intelligent invalidation
 */
class AdvancedCacheService {
  constructor() {
    this.metrics = new CacheMetrics();
    this.dependencies = new Map(); // Track cache dependencies
    this.warmUpQueue = new Set();  // Queue for cache warming
    this.backgroundTasks = new Map(); // Background refresh tasks
  }

  /**
   * Get item from cache with performance tracking
   */
  async get(key, options = {}) {
    const startTime = performance.now();
    
    try {
      const cacheData = localStorage.getItem(key);
      
      if (!cacheData) {
        this.metrics.recordMiss(performance.now() - startTime);
        return null;
      }

      const { data, timestamp, ttl, metadata = {} } = JSON.parse(cacheData);
      const now = Date.now();

      // Check if cache is expired
      if (now - timestamp > ttl) {
        localStorage.removeItem(key);
        this.metrics.recordMiss(performance.now() - startTime);
        
        // Schedule background refresh if enabled
        if (options.backgroundRefresh && metadata.refreshFunction) {
          this.scheduleBackgroundRefresh(key, metadata.refreshFunction);
        }
        
        return null;
      }

      this.metrics.recordHit(performance.now() - startTime);
      
      // Update access time for LRU tracking
      if (CACHE_CONFIG.METRICS.ENABLED) {
        this.updateAccessTime(key);
      }

      return data;
    } catch (error) {
      console.warn('Cache get error:', error);
      this.metrics.recordError();
      return null;
    }
  }

  /**
   * Set item in cache with dependency tracking
   */
  async set(key, data, options = {}) {
    try {
      const {
        ttl = CACHE_CONFIG.TTL.BUDGETS,
        dependencies = [],
        refreshFunction = null,
        tags = []
      } = options;

      const cacheData = {
        data,
        timestamp: Date.now(),
        ttl,
        metadata: {
          refreshFunction: refreshFunction?.name,
          tags,
          version: 1
        }
      };

      localStorage.setItem(key, JSON.stringify(cacheData));
      this.metrics.recordSet();

      // Track dependencies for intelligent invalidation
      if (dependencies.length > 0) {
        this.trackDependencies(key, dependencies);
      }

      // Schedule cache warming for related data
      if (CACHE_CONFIG.WARM_UP.ENABLED) {
        this.scheduleWarmUp(key, data);
      }

    } catch (error) {
      console.warn('Cache set error:', error);
      this.metrics.recordError();
    }
  }

  /**
   * Intelligent cache invalidation based on dependencies
   */
  async invalidate(pattern, options = {}) {
    const { cascade = true, reason = 'manual' } = options;
    const invalidatedKeys = [];

    try {
      // Get all cache keys
      const allKeys = Object.keys(localStorage).filter(key => 
        key.match(pattern) || (typeof pattern === 'string' && key.includes(pattern))
      );

      for (const key of allKeys) {
        localStorage.removeItem(key);
        invalidatedKeys.push(key);
        this.metrics.recordInvalidation();

        // Cancel any background refresh tasks
        if (this.backgroundTasks.has(key)) {
          clearTimeout(this.backgroundTasks.get(key));
          this.backgroundTasks.delete(key);
        }
      }

      // Cascade invalidation to dependent caches
      if (cascade) {
        for (const key of invalidatedKeys) {
          const dependents = this.getDependents(key);
          for (const dependent of dependents) {
            if (!invalidatedKeys.includes(dependent)) {
              localStorage.removeItem(dependent);
              invalidatedKeys.push(dependent);
              this.metrics.recordInvalidation();
            }
          }
        }
      }

      console.log(`Cache invalidated: ${invalidatedKeys.length} keys (reason: ${reason})`);
      return invalidatedKeys;

    } catch (error) {
      console.warn('Cache invalidation error:', error);
      this.metrics.recordError();
      return [];
    }
  }

  /**
   * Cache warming strategy
   */
  async warmUp(keys = []) {
    if (!CACHE_CONFIG.WARM_UP.ENABLED) return;

    for (const key of keys) {
      this.warmUpQueue.add(key);
    }

    // Process warm-up queue with delay
    setTimeout(() => {
      this.processWarmUpQueue();
    }, CACHE_CONFIG.WARM_UP.DELAY);
  }

  /**
   * Get cache statistics and performance metrics
   */
  getMetrics() {
    const stats = this.metrics.getStats();
    const cacheSize = this.getCacheSize();
    const dependencyCount = this.dependencies.size;

    return {
      ...stats,
      cacheSize,
      dependencyCount,
      backgroundTasks: this.backgroundTasks.size,
      warmUpQueue: this.warmUpQueue.size
    };
  }

  /**
   * Clear all cache data
   */
  async clear(options = {}) {
    const { pattern = null, preserveMetrics = false } = options;
    
    try {
      if (pattern) {
        // Clear specific pattern
        const keys = Object.keys(localStorage).filter(key => key.match(pattern));
        keys.forEach(key => localStorage.removeItem(key));
        console.log(`Cleared ${keys.length} cache entries matching pattern: ${pattern}`);
      } else {
        // Clear all cache data
        const cacheKeys = Object.keys(localStorage).filter(key => 
          key.startsWith('budget') || 
          key.startsWith('ledger') || 
          key.startsWith('payee') ||
          key.startsWith('deps_') ||
          key.startsWith('metrics_')
        );
        cacheKeys.forEach(key => localStorage.removeItem(key));
        console.log(`Cleared ${cacheKeys.length} cache entries`);
      }

      // Clear internal state
      this.dependencies.clear();
      this.warmUpQueue.clear();
      this.backgroundTasks.forEach(task => clearTimeout(task));
      this.backgroundTasks.clear();

      if (!preserveMetrics) {
        this.metrics.reset();
      }

    } catch (error) {
      console.warn('Cache clear error:', error);
      this.metrics.recordError();
    }
  }

  /**
   * Private methods
   */

  trackDependencies(key, dependencies) {
    for (const dep of dependencies) {
      if (!this.dependencies.has(dep)) {
        this.dependencies.set(dep, new Set());
      }
      this.dependencies.get(dep).add(key);
    }
  }

  getDependents(key) {
    return Array.from(this.dependencies.get(key) || []);
  }

  scheduleWarmUp(key, data) {
    // Determine related keys to warm up based on data type
    const relatedKeys = this.getRelatedKeys(key, data);
    relatedKeys.forEach(relatedKey => this.warmUpQueue.add(relatedKey));
  }

  getRelatedKeys(key, data) {
    const related = [];
    
    // If it's a budget list, warm up individual budget caches
    if (key === CacheKeys.BUDGETS() && Array.isArray(data.budgets)) {
      data.budgets.forEach(budget => {
        related.push(CacheKeys.BUDGET(budget.id));
        related.push(CacheKeys.BUDGET_BALANCE(budget.id));
      });
    }
    
    // If it's a ledger list, warm up individual ledger caches
    if (key === CacheKeys.LEDGERS() && Array.isArray(data.ledgers)) {
      data.ledgers.forEach(ledger => {
        related.push(CacheKeys.LEDGER(ledger.id));
        related.push(CacheKeys.LEDGER_BALANCE(ledger.id));
      });
    }

    return related;
  }

  async processWarmUpQueue() {
    // Process warm-up queue in batches to avoid overwhelming the system
    const batchSize = 3;
    const queue = Array.from(this.warmUpQueue);
    
    for (let i = 0; i < queue.length; i += batchSize) {
      const batch = queue.slice(i, i + batchSize);
      
      // Process batch with small delay
      setTimeout(() => {
        batch.forEach(key => {
          // Warm-up logic would trigger API calls for missing data
          console.log(`Warming up cache for: ${key}`);
        });
      }, i * 100); // Stagger batches
    }

    this.warmUpQueue.clear();
  }

  scheduleBackgroundRefresh(key, _refreshFunction) {
    if (this.backgroundTasks.has(key)) return;

    const task = setTimeout(async () => {
      try {
        console.log(`Background refresh for: ${key}`);
        // Background refresh logic would re-fetch data
        this.backgroundTasks.delete(key);
      } catch (error) {
        console.warn(`Background refresh failed for ${key}:`, error);
        this.backgroundTasks.delete(key);
      }
    }, 1000); // 1 second delay

    this.backgroundTasks.set(key, task);
  }

  updateAccessTime(key) {
    try {
      const metricsKey = CacheKeys.METRICS(key);
      const metrics = JSON.parse(localStorage.getItem(metricsKey) || '{}');
      metrics.lastAccess = Date.now();
      metrics.accessCount = (metrics.accessCount || 0) + 1;
      localStorage.setItem(metricsKey, JSON.stringify(metrics));
    } catch {
      // Ignore metrics errors
    }
  }

  getCacheSize() {
    try {
      let totalSize = 0;
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          totalSize += localStorage[key].length;
        }
      }
      return `${(totalSize / 1024).toFixed(2)} KB`;
    } catch {
      return 'Unknown';
    }
  }
}

// Create singleton instance
const cacheService = new AdvancedCacheService();

// Export cache service and utilities
export default cacheService;
export { CacheKeys, CACHE_CONFIG };

// Development helper - expose cache service globally in dev mode
if (process.env.NODE_ENV === 'development') {
  window.cacheService = cacheService;
}